import { useEffect, useMemo, useState } from "react";
import type { KapitalAuszahlung, KapitalPayload, KapitalTransaktion } from "@dashboard/shared";
import { Area, ComposedChart, ReferenceArea, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Check, ChevronDown, ChevronRight, Info, List, Lock, RefreshCw, TriangleAlert, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { useEurUsd } from "@/lib/fx";
import { cn, fmtInt } from "@/lib/utils";

/** Kapital (Operator 2026-10-02), unter den Marken-Kacheln: drei Kontostaende in USD - jetzt (Revolut), nach der naechsten
 *  und nach der uebernaechsten Provisionszahlung (eToro und AvaTrade zahlen den Vormonat um den 15. des Folgemonats).
 *  Dazwischen kleingedruckt, was eToro bzw. AvaTrade beitragen. Aufklappbar: Kontoverlauf als Stufenlinie, wenn bis zur
 *  jeweiligen Zahlung jeden Tag so viel ausgegeben wird wie gestern (bzw. im Schnitt der letzten 7 Tage). Unabhaengig vom
 *  gewaehlten Zeitraum. Revolut-Konten in EUR/GBP werden zum EZB-Tageskurs in USD umgerechnet. */

const WIEN = "Europe/Vienna";
const MONAT_LANG = new Intl.DateTimeFormat("de-DE", { month: "long", timeZone: "UTC" });
const DATUM_LANG = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", timeZone: "UTC" });
const DATUM_KURZ = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone: "UTC" });
const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const EURF = new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const usd = (n: number) => USD.format(Math.round(n) === 0 ? 0 : n);
const eur = (n: number) => EURF.format(Math.round(n) === 0 ? 0 : n);
const utc = (iso: string) => new Date(`${iso}T12:00:00Z`);
const ms = (iso: string) => utc(iso).getTime();
const TAG = 86_400_000;

function heuteWien(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: WIEN }).format(new Date());
}
function plusTage(iso: string, n: number): string {
  const d = utc(iso); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}
const tageBis = (von: string, bis: string) => Math.round((ms(bis) - ms(von)) / TAG);
const monatsname = (ym: string) => MONAT_LANG.format(utc(`${ym}-01`));

/** Immer genau zwei Zahlungstermine: der naechste offene Monat und der danach (fehlt einer, steht er mit 0 da). */
function zweiTermine(liste: KapitalAuszahlung[], heute: string): [KapitalAuszahlung, KapitalAuszahlung] {
  const offen = liste.filter((a) => a.auszahlung >= heute).sort((a, b) => a.auszahlung.localeCompare(b.auszahlung));
  const folgemonat = (ym: string) => {
    const [j, m] = ym.split("-").map(Number) as [number, number];
    return m === 12 ? `${j + 1}-01` : `${j}-${String(m + 1).padStart(2, "0")}`;
  };
  const leer = (monat: string): KapitalAuszahlung =>
    ({ monat, auszahlung: `${folgemonat(monat)}-15`, etoro: { cpa: 0, usd: 0 }, avatrade: { cpa: 0, usd: 0 }, summeUsd: 0 });
  const erster = offen[0] ?? leer(heute.slice(0, 7));
  const zweiter = offen.find((a) => a.monat === folgemonat(erster.monat)) ?? leer(folgemonat(erster.monat));
  return [erster, zweiter];
}

type Basis = "gestern" | "woche";
const lesen = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const schreiben = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* egal */ } };

/** EZB-Kurse (Einheiten je 1 EUR) fuer weitere Revolut-Waehrungen (GBP …); USD kommt aus useEurUsd. */
function useKurse(waehrungen: string[]): Record<string, number> {
  const [kurse, setKurse] = useState<Record<string, number>>({});
  const liste = waehrungen.filter((w) => w !== "EUR" && w !== "USD").sort().join(",");
  useEffect(() => {
    if (!liste) return;
    let cancelled = false;
    fetch(`https://api.frankfurter.dev/v1/latest?base=EUR&symbols=${liste}`)
      .then((r) => r.json())
      .then((j: { rates?: Record<string, number> }) => { if (!cancelled && j?.rates) setKurse(j.rates); })
      .catch(() => { /* ohne Kurs bleibt die Waehrung aussen vor und wird angezeigt */ });
    return () => { cancelled = true; };
  }, [liste]);
  return kurse;
}

/** Werbeausgaben aller Konten (Microsoft, ChatGPT, Google) im Zeitraum, in USD. null = nicht ermittelbar. */
async function ausgabenUsd(from: string, to: string, eurUsd: number): Promise<number | null> {
  const [msa, oa, gg] = await Promise.allSettled([
    api.msadsCampaigns({ from, to }), api.openaiCampaigns({ from, to }), api.googleCampaigns({ from, to }),
  ]);
  if (msa.status === "rejected" && oa.status === "rejected") return null;
  const inUsd = (s: number, w?: string) => ((w || "EUR").toUpperCase() === "USD" ? s : s * eurUsd);
  let summe = 0;
  if (msa.status === "fulfilled") summe += msa.value.campaigns.reduce((s, c) => s + c.spend, 0);
  if (oa.status === "fulfilled") {
    for (const a of oa.value.accounts) summe += inUsd(a.campaigns.reduce((x, c) => x + c.spend, 0), a.currency);
  }
  if (gg.status === "fulfilled") summe += inUsd(gg.value.campaigns.reduce((x, c) => x + c.spend, 0), gg.value.account?.currency);
  return summe;
}

export function KapitalKarte() {
  const [d, setD] = useState<KapitalPayload | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState(() => lesen("kapital:offen") === "1");
  const [basis, setBasis] = useState<Basis>(() => (lesen("kapital:basis") === "woche" ? "woche" : "gestern"));
  const [rate, setRate] = useState<{ gestern: number | null; woche: number | null } | null>(null);
  // Zugangscode fuer die Bankdaten (Dashboard ist oeffentlich erreichbar); einmal je Browser eingeben.
  const [pin, setPin] = useState(() => lesen("kapital:pin") ?? "");
  const [sperre, setSperre] = useState<"pin" | "gesperrt" | null>(null);
  const [txOffen, setTxOffen] = useState(() => lesen("kapital:tx") === "1");
  const [neu, setNeu] = useState(0);
  // Ganze Karte auf-/zuklappbar (Operator 2026-10-09): Standard zugeklappt = eine schmale Zeile mit den drei Kontostaenden.
  const [karteOffen, setKarteOffen] = useState(() => lesen("kapital:karte") === "1");
  const karteUmschalten = () => setKarteOffen((o) => { schreiben("kapital:karte", o ? "0" : "1"); return !o; });
  const eurUsd = useEurUsd();
  const heute = heuteWien();

  useEffect(() => {
    let cancelled = false;
    const load = () => api.kapital(pin)
      .then((r) => { if (!cancelled) { setD(r); setFehler(null); setSperre(null); } })
      .catch((e: Error) => {
        if (cancelled) return;
        if (e.message === "pin" || e.message === "gesperrt") { setSperre(e.message); setD(null); setFehler(null); }
        else setFehler(e.message);
      });
    load();
    const id = setInterval(load, 3 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pin, neu]);

  useEffect(() => {
    let cancelled = false;
    const gestern = plusTage(heute, -1);
    Promise.all([ausgabenUsd(gestern, gestern, eurUsd), ausgabenUsd(plusTage(heute, -7), gestern, eurUsd)])
      .then(([g, w]) => { if (!cancelled) setRate({ gestern: g, woche: w === null ? null : w / 7 }); })
      .catch(() => { if (!cancelled) setRate({ gestern: null, woche: null }); });
    return () => { cancelled = true; };
  }, [heute, eurUsd]);

  const rev = d?.revolut;
  const konten = rev?.konten ?? [];
  const kurse = useKurse([...new Set(konten.map((k) => k.waehrung))]);
  // Betrag einer Kontowaehrung in USD: USD direkt, EUR ueber den EZB-Kurs, andere ueber EUR.
  const inUsd = (betrag: number, w: string): number | null =>
    w === "USD" ? betrag : w === "EUR" ? betrag * eurUsd : kurse[w] ? (betrag / kurse[w]!) * eurUsd : null;
  const verbunden = Boolean(rev?.verbunden && konten.length && !rev?.fehler);
  const jetzt = verbunden ? konten.reduce((s, k) => s + (inUsd(k.saldo, k.waehrung) ?? 0), 0) : null;
  const schulden = (d?.schuldenEur ?? 0) * eurUsd;
  const [t1, t2] = useMemo(() => zweiTermine(d?.auszahlungen ?? [], heute), [d, heute]);
  const stand1 = jetzt === null ? null : jetzt + t1.summeUsd;
  const stand2 = stand1 === null ? null : stand1 + t2.summeUsd;

  const tagesrate = rate ? rate[basis] : undefined;
  const plan = useMemo(() => {
    if (jetzt === null || tagesrate == null) return null;
    const vor1 = jetzt - Math.max(0, tageBis(heute, t1.auszahlung)) * tagesrate;
    const vor2 = vor1 + t1.summeUsd - Math.max(0, tageBis(t1.auszahlung, t2.auszahlung)) * tagesrate;
    return { vor1, vor2, reichweite: tagesrate > 0 ? Math.floor(jetzt / tagesrate) : null };
  }, [jetzt, tagesrate, t1, t2, heute]);

  const umschalten = () => setOffen((o) => { schreiben("kapital:offen", o ? "0" : "1"); return !o; });
  const basisSetzen = (b: Basis) => { setBasis(b); schreiben("kapital:basis", b); };
  const standZeit = rev?.stand ? new Date(rev.stand).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" }) : null;
  const kontenText = konten.map((k) => `${k.name} (${k.waehrung}): ${new Intl.NumberFormat("de-AT", { style: "currency", currency: k.waehrung }).format(k.saldo)}`).join("\n");
  const kopfHinweis = [
    kontenText, `1 € = ${eurUsd.toFixed(3)} $ (EZB)`,
    d?.schuldenEur ? `Schulden: ${eur(d.schuldenEur)} (${usd(schulden)})` : "",
    "Provisionen laut Tracker: eToro Postback-Betrag je Trader, AvaTrade 750 $ (Guadeloupe/Martinique 350 $). Auszahlung um den 15. des Folgemonats.",
  ].filter(Boolean).join("\n");

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card" data-section="Kapital">
      {/* Aufbau nach Vorlage (Operator 2026-10-09): Kopf mit Waehrung und Revolut-Stand, drei Spalten Aktuell / erwartete
          Kontostaende an den naechsten zwei Zahltagen, darunter Liquiditaetscheck und letzte Transaktionen. */}
      <div className={cn("flex items-center justify-between gap-3 px-5", karteOffen ? "pt-3" : "py-2.5")}>
        <button type="button" onClick={karteUmschalten} aria-expanded={karteOffen} data-kapital="karte"
          className="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <h3 className="text-[17px] tracking-tight">Kapital</h3>
          {karteOffen && <span className="rounded-full bg-muted px-1.5 py-px text-[10.5px] text-muted-foreground">USD</span>}
          {!karteOffen && !sperre && (
            <span className="ml-1.5 flex min-w-0 items-baseline gap-2.5 truncate text-[12px] tabular-nums text-muted-foreground" data-kapital="kurz">
              <span title="Kontostand heute">Jetzt <span className=" text-foreground">{jetzt !== null ? usd(jetzt) : "–"}</span></span>
              <span>{DATUM_KURZ.format(utc(t1.auszahlung))} <span className=" text-foreground">{stand1 !== null ? usd(stand1) : "–"}</span></span>
              <span className="hidden sm:inline">{DATUM_KURZ.format(utc(t2.auszahlung))} <span className=" text-foreground">{stand2 !== null ? usd(stand2) : "–"}</span></span>
            </span>
          )}
          <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", karteOffen && "rotate-180")} />
        </button>
        <div className="flex shrink-0 items-center gap-2 text-[12px] tabular-nums text-muted-foreground">
          {!sperre && karteOffen && (
            <span className="inline-flex cursor-help items-center gap-1.5" title={kopfHinweis}>
              {verbunden && <span className="text-[13px] leading-none text-foreground">R</span>}
              {verbunden ? <>Revolut · {standZeit ?? ""}</> : rev?.fehler ? "Revolut-Fehler" : d ? "Revolut nicht verbunden" : ""}
            </span>
          )}
          <button type="button" onClick={() => setNeu((x) => x + 1)} aria-label="Aktualisieren" title="Aktualisieren"
            className="rounded p-1 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {karteOffen && (<>
      {sperre ? (
        <CodeEingabe falsch={Boolean(pin)} gesperrt={sperre === "gesperrt"}
          onCode={(c) => { schreiben("kapital:pin", c); setPin(c); }} />
      ) : d === null && !fehler ? (
        <div className="px-5 py-5 text-sm text-muted-foreground">Lädt …</div>
      ) : (
        <div className="grid grid-cols-1 px-5 pb-2.5 md:grid-cols-3">
          <Aktuell betrag={jetzt} schulden={schulden} eurUsd={eurUsd}
            hinweis={verbunden ? `auf Revolut\n${kontenText}` : "Revolut nicht verbunden"} />
          <Erwartet t={t1} betrag={stand1} schulden={schulden} eurUsd={eurUsd}
            plus={jetzt === null ? t1.summeUsd : undefined}
            hinweis={`${DATUM_LANG.format(utc(t1.auszahlung))}: Kontostand nach der Zahlung für ${monatsname(t1.monat)}`} />
          <Erwartet t={t2} betrag={stand2} schulden={schulden} eurUsd={eurUsd} laufend={t2.monat === heute.slice(0, 7)}
            plus={jetzt === null ? t1.summeUsd + t2.summeUsd : undefined}
            hinweis={`${DATUM_LANG.format(utc(t2.auszahlung))}: Kontostand nach der Zahlung für ${monatsname(t2.monat)}`} />
        </div>
      )}

      {d !== null && (
        <div className="border-t border-border">
          <button type="button" onClick={umschalten} aria-expanded={offen} data-kapital="reicht"
            title="Bleibt das Konto bis zur jeweiligen Zahlung im Plus, wenn jeden Tag so viel ausgegeben wird wie gestern bzw. im Schnitt der letzten 7 Tage (alle Werbekonten)? Neue Provisionen ab heute sind nicht eingerechnet."
            className="flex w-full items-center gap-2.5 px-5 py-1.5 text-left text-[13px] hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Wallet className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="">Liquiditätscheck</span>
            <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", offen && "rotate-90")} />
            <span className="ml-auto flex items-center gap-2">
              <Ampel datum={t1.auszahlung} wert={plan?.vor1} />
              <Ampel datum={t2.auszahlung} wert={plan?.vor2} />
            </span>
          </button>
          {offen && (
            <div className="px-5 pb-3" data-kapital="reicht-details">
              <div className="flex items-center gap-3 text-[12px] text-muted-foreground">
                <span className="inline-flex overflow-hidden rounded-md border border-border" title="Tagesausgaben aller Werbekonten (Microsoft, ChatGPT, Google)">
                  {(["gestern", "woche"] as Basis[]).map((b) => (
                    <button key={b} type="button" onClick={() => basisSetzen(b)} aria-pressed={basis === b}
                      className={cn("px-2.5 py-1 tabular-nums", basis === b ? "bg-foreground text-background" : "hover:bg-muted/60")}>
                      {b === "gestern" ? "gestern" : "Ø 7 Tage"} {rate?.[b] != null ? `${usd(rate[b]!)}/Tag` : rate ? "–" : "…"}
                    </button>
                  ))}
                </span>
                {plan?.reichweite != null && (
                  <span className="ml-auto cursor-help tabular-nums" title="So lange reicht das Geld auf Revolut ohne jede Zahlung">
                    Reichweite <span className=" text-foreground">{fmtInt(plan.reichweite)} Tage</span>
                  </span>
                )}
              </div>
              {jetzt !== null && tagesrate != null ? (
                <Verlauf heute={heute} start={jetzt} rate={tagesrate} t1={t1} t2={t2} schulden={schulden} />
              ) : (
                <div className="py-6 text-[12px] text-muted-foreground">{jetzt === null ? "Revolut nicht verbunden" : rate ? "Ausgaben nicht verfügbar" : "Lädt …"}</div>
              )}
            </div>
          )}
        </div>
      )}
      {d !== null && (d.transaktionen?.length ?? 0) > 0 && (
        <div className="border-t border-border">
          <button type="button" aria-expanded={txOffen} data-kapital="transaktionen"
            onClick={() => setTxOffen((o) => { schreiben("kapital:tx", o ? "0" : "1"); return !o; })}
            className="flex w-full items-center gap-2.5 px-5 py-1.5 text-left text-[13px] hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <List className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="">Letzte Transaktionen</span>
            <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", txOffen && "rotate-90")} />
            <TxSumme liste={d.transaktionen!} inUsd={inUsd} heute={heute} />
          </button>
          {txOffen && <TxListe liste={d.transaktionen!} inUsd={inUsd} />}
        </div>
      )}
      </>)}
      {fehler && <div className="px-5 pb-3 text-xs text-destructive">Kapital: {fehler}</div>}
    </section>
  );
}

/** Zugangscode-Eingabe: die Kapitaldaten (Kontostand, Transaktionen) gibt der Server nur mit Code heraus. */
function CodeEingabe({ falsch, gesperrt, onCode }: { falsch: boolean; gesperrt: boolean; onCode: (c: string) => void }) {
  const [wert, setWert] = useState("");
  return (
    <form className="flex items-center gap-2 px-5 pb-3 pt-2.5" data-kapital="code"
      onSubmit={(e) => { e.preventDefault(); if (wert.trim()) onCode(wert.trim()); }}>
      <Lock className="h-4 w-4 text-muted-foreground" />
      <input type="password" inputMode="numeric" autoComplete="off" value={wert} onChange={(e) => setWert(e.target.value)}
        placeholder="Zugangscode" aria-label="Zugangscode"
        className="w-36 rounded-md border border-border bg-background px-2.5 py-1 text-[13px] tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      <button type="submit" className="rounded-md bg-foreground px-3 py-1 text-[13px] text-background hover:opacity-90">Öffnen</button>
      {(falsch || gesperrt) && (
        <span className="text-[12px] text-destructive">{gesperrt ? "zu viele Versuche – 15 Min. warten" : "Code falsch"}</span>
      )}
    </form>
  );
}

const TX_ZEIT = new Intl.DateTimeFormat("de-AT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: WIEN });
const TX_TAG = new Intl.DateTimeFormat("de-AT", { day: "2-digit", month: "2-digit", timeZone: WIEN });
const geld = (n: number, w: string) => new Intl.NumberFormat("en-US", { style: "currency", currency: w || "EUR", maximumFractionDigits: 2 }).format(n);
const txUsd = (t: KapitalTransaktion, inUsd: (b: number, w: string) => number | null) =>
  t.buchungen.reduce((s, b) => s + (inUsd(b.betrag - b.gebuehr, b.waehrung) ?? 0), 0);

/** Kopfzeile der Transaktionen: Saldo der Bewegungen der letzten 7 Tage in USD, Details im Hover. */
function TxSumme({ liste, inUsd, heute }: { liste: KapitalTransaktion[]; inUsd: (b: number, w: string) => number | null; heute: string }) {
  const grenze = ms(plusTage(heute, -7));
  const woche = liste.filter((t) => new Date(t.zeit).getTime() >= grenze);
  const rein = woche.reduce((s, t) => s + Math.max(0, txUsd(t, inUsd)), 0);
  const raus = woche.reduce((s, t) => s + Math.min(0, txUsd(t, inUsd)), 0);
  return (
    <span className="ml-auto flex items-baseline gap-3 text-[12px] tabular-nums text-muted-foreground"
      title={`Letzte 7 Tage, ${woche.length} Buchungen\nEingang ${usd(rein)} · Ausgang ${usd(raus)}`}>
      Letzte 7 Tage
      <span className="text-[13px] text-foreground">{rein + raus < 0 ? "−" : "+"}{usd(Math.abs(rein + raus))}</span>
    </span>
  );
}

/** Kompakte Liste, auf breiten Bildschirmen zweispaltig (kurze Wege zwischen Text und Betrag). */
function TxListe({ liste, inUsd }: { liste: KapitalTransaktion[]; inUsd: (b: number, w: string) => number | null }) {
  return (
    <div className="grid grid-cols-1 px-5 pb-3" data-kapital="tx-liste">
      {liste.slice(0, 30).map((t, i) => {
        const b = t.buchungen[0];
        const offen = t.status !== "completed";
        const betrag = b?.betrag ?? 0;
        const hinweis = [
          TX_ZEIT.format(new Date(t.zeit)), t.text, ...t.buchungen.map((x) => `${x.konto}: ${geld(x.betrag, x.waehrung)}`
            + (x.gebuehr ? ` (Gebühr ${geld(x.gebuehr, x.waehrung)})` : "") + (x.saldo != null ? ` · danach ${geld(x.saldo, x.waehrung)}` : "")),
          `≈ ${usd(txUsd(t, inUsd))}`, offen ? "noch nicht gebucht" : "",
        ].filter(Boolean).join("\n");
        return (
          <div key={`${t.zeit}-${i}`} title={hinweis} data-kapital="tx"
            className="grid cursor-default grid-cols-[38px_minmax(0,1fr)_auto] items-baseline gap-2.5 border-b border-border/50 py-1 text-[12.5px] hover:bg-muted/40">
            <span className="tabular-nums text-muted-foreground">{TX_TAG.format(new Date(t.zeit))}</span>
            <span className={cn("truncate", offen && "text-muted-foreground")}>{t.text}{offen && <span className="ml-1.5 text-[10.5px]">· offen</span>}</span>
            <span className={cn("tabular-nums", betrag > 0 && "rounded bg-emerald-500/10 px-1")}>
              {betrag > 0 ? "+" : betrag < 0 ? "−" : ""}{geld(Math.abs(betrag), b?.waehrung ?? "EUR")}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Farben (Operator 2026-10-09): Schrift nur schwarz und normal (nichts fett), Farbe nur als 90 % transparenter Hintergrund. */
const GRUEN = "bg-emerald-500/10 text-foreground";
const ROT = "bg-rose-500/10 text-foreground";

/** Netto-Zeile: Kontostand nach Abzug der Schulden, gruen im Plus, rot im Minus. */
function Netto({ netto, gross, info }: { netto: number; gross?: boolean; info?: string }) {
  return (
    <div className={cn("flex items-center gap-1.5 whitespace-nowrap tabular-nums", gross ? "mt-1.5" : "mt-1")}>
      <span className="text-[12.5px] text-muted-foreground">Netto</span>
      {info && <Info className="h-3 w-3 cursor-help text-muted-foreground" aria-label={info} />}
      <span className={cn("rounded-md px-1.5 py-px text-[13px]", netto < 0 ? ROT : GRUEN)}>
        {netto < 0 ? "−" : "+"}{usd(Math.abs(netto))}
      </span>
    </div>
  );
}

/** Erste Spalte: Kontostand heute (Revolut) gross, darunter netto nach Schulden. Details beim Darueberfahren. */
function Aktuell({ betrag, schulden, eurUsd, hinweis }: { betrag: number | null; schulden: number; eurUsd: number; hinweis: string }) {
  const netto = betrag !== null && schulden > 0 ? betrag - schulden : null;
  return (
    <div className="min-w-0 cursor-default py-1.5 md:pr-4" data-kapital="stand"
      title={[hinweis, betrag !== null ? `≈ ${eur(betrag / eurUsd)}` : ""].filter(Boolean).join("\n")}>
      <div className="mt-3.5 text-[10.5px] uppercase tracking-wide text-muted-foreground">Aktuell</div>
      <div className={cn("mt-1 whitespace-nowrap text-[25px] leading-none tracking-tight", betrag === null && "text-muted-foreground")}>
        {betrag !== null ? usd(betrag) : "–"}
      </div>
      {netto !== null && <Netto netto={netto} gross info={`nach Abzug der Schulden (${usd(schulden)})`} />}
      <div className="mt-1 text-[11.5px] text-muted-foreground">Kontostand heute</div>
    </div>
  );
}

/** Spalte je Zahltag: Kennzeichen "Erwartet", Datum, Kontostand nach der Zahlung, netto, darunter was eToro und AvaTrade zahlen. */
function Erwartet({ t, betrag, schulden, eurUsd, plus, hinweis, laufend }: {
  t: KapitalAuszahlung; betrag: number | null; schulden: number; eurUsd: number; plus?: number; hinweis: string; laufend?: boolean;
}) {
  const netto = betrag !== null && schulden > 0 ? betrag - schulden : null;
  const wert = betrag ?? plus;
  // Bereits eingegangene Partnerzahlung (Revolut-Eingang, Operator 2026-10-13): steckt im Kontostand, zaehlt hier nicht mehr.
  const partner = (name: string, x: KapitalAuszahlung["etoro"]) => x.eingegangen
    ? `${name}: eingegangen ${DATUM_KURZ.format(utc(x.eingegangen.datum))} · ${!x.eingegangen.waehrung || x.eingegangen.waehrung === "USD" ? usd(x.eingegangen.betrag) : `${fmtInt(x.eingegangen.betrag)} ${x.eingegangen.waehrung}`} (erwartet ${usd(x.usd)})`
    : `${name}: ${fmtInt(x.cpa)} Trader · ${usd(x.usd)}`;
  const zahlung = `Provisionen ${monatsname(t.monat)}, Auszahlung ${t.ueberfaellig && t.planTag ? `${DATUM_LANG.format(utc(t.planTag))}, noch nicht eingegangen` : DATUM_LANG.format(utc(t.auszahlung))}${laufend ? " (Monat läuft noch)" : ""}\n`
    + `${partner("eToro", t.etoro)}\n${partner("AvaTrade", t.avatrade)}`;
  const zeile = (name: string, x: KapitalAuszahlung["etoro"]) => (
    <div className="flex justify-between text-muted-foreground">
      <span>{name}</span>
      {x.eingegangen
        ? <span className="text-muted-foreground">✓ {DATUM_KURZ.format(utc(x.eingegangen.datum))}</span>
        : <span className="text-foreground">+{usd(x.usd)}</span>}
    </div>
  );
  return (
    <div className="min-w-0 cursor-default border-t border-border py-1.5 md:border-l md:border-t-0 md:px-4 md:last:pr-0" data-kapital="stand"
      title={[hinweis, wert !== undefined ? `≈ ${eur(wert / eurUsd)}` : "", netto !== null ? `nach Abzug der Schulden (${usd(schulden)}): ${usd(netto)}` : ""].filter(Boolean).join("\n")}>
      <span className="inline-block rounded-full bg-[#3563d6]/10 px-1.5 py-px text-[9.5px] uppercase tracking-wide text-foreground">
        Erwartet
      </span>
      <div className="mt-1 text-[12.5px]">{t.ueberfaellig && t.planTag ? `seit ${DATUM_LANG.format(utc(t.planTag))}` : DATUM_LANG.format(utc(t.auszahlung))}</div>
      <div className={cn("mt-0.5 whitespace-nowrap text-[20px] leading-none tracking-tight", betrag === null && "text-muted-foreground")}>
        {betrag !== null ? usd(betrag) : plus !== undefined ? `+${usd(plus)}` : "–"}
      </div>
      {netto !== null && <Netto netto={netto} />}
      <div className="mt-2 space-y-px border-t border-border pt-1.5 text-[12px] tabular-nums" data-kapital="zuwachs" title={zahlung}>
        {zeile("eToro", t.etoro)}
        {zeile("AvaTrade", t.avatrade)}
      </div>
    </div>
  );
}

type Punkt = { t: number; wert: number; zahlung?: number };
const usdK = (n: number) => (Math.abs(n) >= 1000 ? `${n < 0 ? "−" : ""}$${(Math.abs(n) / 1000).toFixed(1)}k` : usd(n));

/** Kontoverlauf als Stufenlinie: faellt jeden Tag um die Tagesausgaben, springt am Zahltag um die Provisionen nach oben.
 *  Eine Reihe (keine Legende); Nulllinie und Schulden-Linie als beschriftete Grenzen; Werte nur an den Tiefpunkten vor
 *  jeder Zahlung und an den Spruengen, alles andere im Tooltip. */
function Verlauf({ heute, start, rate, t1, t2, schulden }: {
  heute: string; start: number; rate: number; t1: KapitalAuszahlung; t2: KapitalAuszahlung; schulden: number;
}) {
  const daten = useMemo(() => {
    const p: Punkt[] = [];
    const ende = tageBis(heute, t2.auszahlung);
    let stand = start;
    for (let i = 0; i <= ende; i++) {
      const tag = plusTage(heute, i);
      if (i > 0) stand -= rate;
      const zahl = tag === t1.auszahlung ? t1.summeUsd : tag === t2.auszahlung ? t2.summeUsd : 0;
      p.push({ t: ms(tag), wert: stand });
      if (zahl) { stand += zahl; p.push({ t: ms(tag), wert: stand, zahlung: zahl }); }
    }
    return p;
  }, [heute, start, rate, t1, t2]);
  const werte = [...daten.map((p) => p.wert), schulden];
  const roh = Math.max(...werte) - Math.min(0, ...werte);
  // Etwa vier Teilstriche: kleinster "runder" Schritt, der mindestens ein Viertel der Spanne abdeckt.
  const schritt = [1_000, 2_000, 5_000, 10_000, 20_000, 25_000, 50_000, 100_000].find((x) => x >= roh / 4) ?? 100_000;
  const unten = Math.floor(Math.min(0, ...werte) / schritt) * schritt;
  const oben = Math.ceil((Math.max(...werte) * 1.08) / schritt) * schritt;
  const yTicks: number[] = [];
  for (let v = unten; v <= oben; v += schritt) yTicks.push(v);
  const amTag = (iso: string) => daten.filter((p) => p.t === ms(iso));
  const vor1 = amTag(t1.auszahlung)[0]!, nach1 = amTag(t1.auszahlung).at(-1)!;
  const vor2 = amTag(t2.auszahlung)[0]!, nach2 = amTag(t2.auszahlung).at(-1)!;
  const xTicks = [ms(heute), ms(t1.auszahlung), ms(t2.auszahlung)];
  const text = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };
  const wertLabel = (txt: string, position: "top" | "right" | "bottom") => ({ value: txt, position, offset: 8, style: { ...text, fill: "hsl(var(--foreground))" } });
  const punkt = { r: 4, fill: "currentColor", stroke: "hsl(var(--card))", strokeWidth: 2 };
  return (
    <div className="mt-2 h-[190px] text-[#2a78d6] dark:text-[#3987e5]" data-kapital="verlauf"
      role="img" aria-label={`Kontoverlauf: heute ${usd(start)}, vor der Zahlung am ${DATUM_KURZ.format(utc(t1.auszahlung))} ${usd(vor1.wert)}, danach ${usd(nach1.wert)}, am ${DATUM_KURZ.format(utc(t2.auszahlung))} ${usd(nach2.wert)}`}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={daten} margin={{ top: 20, right: 26, bottom: 0, left: 0 }}>
          <XAxis dataKey="t" type="number" scale="time" domain={[xTicks[0]!, xTicks[2]!]} ticks={xTicks}
            tickFormatter={(v: number) => (v === xTicks[0] ? "heute" : DATUM_KURZ.format(new Date(v)))}
            tick={text} tickLine={false} axisLine={false} padding={{ left: 4, right: 4 }} />
          <YAxis domain={[unten, oben]} ticks={yTicks} tickFormatter={(v: number) => (v === 0 ? "$0" : usdK(v).replace(".0k", "k"))}
            width={42} tick={text} tickLine={false} axisLine={false} />
          {yTicks.filter((v) => v !== 0).map((v) => <ReferenceLine key={v} y={v} stroke="hsl(var(--border))" strokeOpacity={0.7} />)}
          {unten < 0 && <ReferenceArea y1={unten} y2={0} fill="#d03b3b" fillOpacity={0.06} />}
          <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.6} />
          {schulden > 0 && (
            <ReferenceLine y={schulden} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.6}
              label={{ value: `Schulden ${usdK(schulden)}`, position: "insideBottomLeft", offset: 6, style: text }} />
          )}
          <Tooltip content={<VerlaufTooltip schulden={schulden} />} cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.4 }} isAnimationActive={false} />
          <Area type="linear" dataKey="wert" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" fill="currentColor" fillOpacity={0.1}
            isAnimationActive={false} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: "hsl(var(--card))", fill: "currentColor" }} />
          <ReferenceDot x={vor1.t} y={vor1.wert} {...punkt} label={wertLabel(usdK(vor1.wert), "right")} />
          <ReferenceDot x={nach1.t} y={nach1.wert} {...punkt} label={wertLabel(`+${usdK(t1.summeUsd)}`, "top")} />
          {/* Gibt es am zweiten Termin einen Sprung, steht sein Betrag oben - der Tiefpunkt davor dann unten. */}
          <ReferenceDot x={vor2.t} y={vor2.wert} {...punkt} label={wertLabel(usdK(vor2.wert), t2.summeUsd > 0 ? "bottom" : "top")} />
          {t2.summeUsd > 0 && <ReferenceDot x={nach2.t} y={nach2.wert} {...punkt} label={wertLabel(`+${usdK(t2.summeUsd)}`, "top")} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function VerlaufTooltip({ active, payload, schulden }: { active?: boolean; payload?: { payload: Punkt }[]; schulden: number }) {
  if (!active || !payload?.length) return null;
  const p = payload[payload.length - 1]!.payload;
  const netto = p.wert - schulden;
  return (
    <div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-[12px] shadow-sm">
      <div className=" tabular-nums text-foreground">{usd(p.wert)}</div>
      {schulden > 0 && <div className="tabular-nums text-muted-foreground">netto {netto < 0 ? "−" : "+"}{usd(Math.abs(netto))}</div>}
      <div className="text-muted-foreground">{DATUM_LANG.format(new Date(p.t))}{p.zahlung ? ` · +${usd(p.zahlung)} Provisionen` : ""}</div>
    </div>
  );
}

function Ampel({ datum, wert }: { datum: string; wert: number | undefined }) {
  const kurz = DATUM_KURZ.format(utc(datum));
  if (wert === undefined) {
    return <span className="rounded-full border border-border px-2.5 py-0.5 text-[12px] tabular-nums text-muted-foreground">Bis {kurz.replace(/\.$/, "")}.</span>;
  }
  const ok = wert >= 0;
  return (
    <span title={`Kontostand am Tag vor der Zahlung (${DATUM_LANG.format(utc(plusTage(datum, -1)))}): ${usd(wert)}`}
      className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] tabular-nums",
        ok ? GRUEN : ROT)}>
      {ok ? <Check className="h-3 w-3" /> : <TriangleAlert className="h-3 w-3" />}
      Bis {kurz.replace(/\.$/, "")}.{ok ? "" : ` −${usd(-wert)}`}
    </span>
  );
}
